import { useSuspenseQuery, UseSuspenseQueryOptions, UseSuspenseQueryResult, QueryKey } from '@tanstack/react-query'
import { ApiResult } from '../types'
import { queryFnWithApiResult } from '../utils'

type UsePortalSuspenseQueryOptions<TData, TSelect = TData | null, TQueryKey extends QueryKey = QueryKey> = Omit<
  UseSuspenseQueryOptions<TData | null, Error, TSelect, TQueryKey>,
  'queryFn'
> & {
  queryFn: () => Promise<ApiResult<TData> | TData>
}

/**
 * A wrapper around react-query's useSuspenseQuery that automatically handles ApiResult unwrapping.
 * The queryFn should return a Promise<ApiResult<T>> or a plain Promise<T>.
 *
 * Please pair with an exported queryKey factory for easy invalidation.
 */
const usePortalSuspenseQuery = <TData, TSelect = TData | null, TQueryKey extends QueryKey = QueryKey>(
  options: UsePortalSuspenseQueryOptions<TData, TSelect, TQueryKey>
): UseSuspenseQueryResult<TSelect, Error> => {
  const { queryFn, ...rest } = options
  return useSuspenseQuery<TData | null, Error, TSelect, TQueryKey>({
    ...rest,
    queryFn: queryFnWithApiResult(queryFn),
  })
}

export default usePortalSuspenseQuery
